import { useState } from "react"

import useToggleFileArchivedStatus from "@/components/upload/hooks/useToggleFileArchivedStatus"
import type { File } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { NotificationType } from "@/schemas/notification"
import { getNextFileAction } from "@/screens/Companies/edit/components/info/components/utils"
import { useCompanyFilesContext } from "@/screens/Companies/edit/components/info/context/CompanyFilesContext"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseFileStatusChangeProps {
  isChangeStatusDialogOpen: boolean
  handleFileStatusChange: () => Promise<void>
  handleFileStatusChangeConfirmationDialogOpen: (file: File) => void
  onCloseConfirmationDialog: () => void
  isChangingStatus: boolean
  nextFileStatusAction: string
}

const useFileStatusChange = (): UseFileStatusChangeProps => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { isActiveCompany, onRefresh } = useCompanyFilesContext()

  const { removeSingleFile, removingInProgress } = useFilesRemove()
  const { toggleFileArchivedStatus, isUpdatingStatus } = useToggleFileArchivedStatus()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const isChangeStatusDialogOpen = Boolean(selectedFile)

  const handleFileStatusChangeConfirmationDialogOpen = (file: File) => {
    setSelectedFile(file)
  }

  const onCloseConfirmationDialog = () => {
    setSelectedFile(null)
  }

  const handleFileStatusChange = async () => {
    if (!selectedFile) {
      return
    }
    if (isActiveCompany) {
      await toggleFileArchivedStatus({ input: { fileId: selectedFile.fileId! } })
    } else {
      await removeSingleFile(selectedFile.fileId!)
    }

    const isArchived = selectedFile.isArchived

    const nextAction = isActiveCompany ? (isArchived ? "unarchive" : "archive") : "remove"
    notify({
      type: NotificationType.Success,
      silent: false,
      content: `File successfully ${nextAction}d`,
    })
    onCloseConfirmationDialog()
    onRefresh()
  }

  const nextFileStatusAction = selectedFile
    ? getNextFileAction({
        isActiveCompany,
        isArchived: selectedFile.isArchived!,
      })
    : ""

  return {
    isChangeStatusDialogOpen,
    handleFileStatusChange,
    handleFileStatusChangeConfirmationDialogOpen,
    onCloseConfirmationDialog,
    isChangingStatus: removingInProgress || isUpdatingStatus,
    nextFileStatusAction,
  }
}

export default useFileStatusChange
